'use client';

import React, { useCallback, useState, useEffect } from 'react';
import { Solicitud } from '@/types/solicitud';
import SolicitudManagement from './SolicitudManagement';
import { solicitudService } from '@/lib/api/solicitudService';
import { getCurrentUser } from '@/services/authService';
import ErrorMessage from '@/components/common/ErrorMessage';

/**
 * Módulo Por Fondear:
 * - estatus = "FIRMAR CONTRATO"
 * - contratoDocumento != 'Ok' (solo las que no tienen contrato OK)
 * - Permite editar campos y actualizar el estado del contrato
 * - Al actualizar contratoDocumento a "Ok", la solicitud sale del módulo
 * - Filas en ROJO cuando el contrato NO cumple
 */
const PorFondearManagement: React.FC = () => {
  // Función para resaltar en rojo las solicitudes donde el contrato no cumple
  const getRowClassName = useCallback((solicitud: Solicitud): string | undefined => {
    const contratoDoc = (solicitud.contratoDocumento || '').toLowerCase();
    // Si el contrato es "No Cumple" o "No", resaltar en rojo
    if (contratoDoc === 'no cumple' || contratoDoc === 'no') {
      return 'bg-red-100 border-l-4 border-red-500 text-red-800';
    }
    return undefined;
  }, []);
  const [initialSolicitudes, setInitialSolicitudes] = useState<Solicitud[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPorFondear = useCallback(async () => {
    setLoading(true);
    setError(null);
    const currentUser = getCurrentUser();
    if (!currentUser || !currentUser.ownerID) {
      setError("No se pudo obtener el ID del usuario actual.");
      setLoading(false);
      return;
    }
    try {
      const response = await solicitudService.getPorFondear(currentUser.ownerID);
      if (response.data) {
        setInitialSolicitudes(response.data);
      } else {
        setError(response.error || "Error al cargar solicitudes de Por Fondear.");
      }
    } catch (err: any) {
      setError(err.message || "Error de red al cargar solicitudes.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPorFondear();
  }, [fetchPorFondear]);

  if (loading) {
    return <div className="text-center py-4">Cargando solicitudes...</div>;
  }

  if (error) {
    return <ErrorMessage message={error} />;
  }

  return (
    <SolicitudManagement
      fixedStatus="FIRMAR CONTRATO"
      title="Por Fondear"
      modulo="por-fondear"
      initialSolicitudes={initialSolicitudes}
      customFetch={fetchPorFondear}
      showNewButton={false}
      montoColumn="autorizado"
      rowClassName={getRowClassName}
    />
  );
};

export default PorFondearManagement;
